import React, { useEffect, useRef } from "react";
import { Spinner, Modal } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Folder2, FileEarmark, Trash, CloudUpload, FolderPlus, Search, Archive, Share } from "react-bootstrap-icons";
import { filesService } from "../../services/files-service";
import { colors, ConfirmModal } from "../../libs";
import { confirmAlert } from "react-confirm-alert";
import { GeneralMultiPartUploader } from "../../libs/utils/general-multipart-uploader";
import * as Styled from "./FilesManager.styled";

interface IFileItem {
    itemId: string;
    itemType: "FILE" | "FOLDER";
    originalFilename?: string;
    folderName?: string;
    folderId: string;
    selfFolderId?: string;
    fileId?: string;
    objectSize?: number;
    mimeType?: string;
    createdAt: string;
    downloadUrl?: string;
    shareToken?: string;
    shareEnabled?: boolean;
}

interface IUploadProgress {
    fileId: string;
    fileName: string;
    percentage: number;
}

interface FilesManagerProps {
    clientId: string;
    category?: string;
}

export const FilesManager: React.FC<FilesManagerProps> = ({
    clientId,
    category = "GENERAL",
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [items, setItems] = React.useState<IFileItem[]>([]);
    const [folderStack, setFolderStack] = React.useState<{ id: string; name: string }[]>(() => {
        const fid = searchParams.get("folderId");
        return fid && fid !== "ROOT" ? [{ id: fid, name: "..." }] : [];
    });
    const [loading, setLoading] = React.useState(false);
    const [newFolderName, setNewFolderName] = React.useState("");
    const [showNewFolder, setShowNewFolder] = React.useState(false);
    const [uploadProgressList, setUploadProgressList] = React.useState<IUploadProgress[]>([]);
    const [highlightedItemId, setHighlightedItemId] = React.useState<string | null>(null);

    const [showSearchModal, setShowSearchModal] = React.useState(false);
    const [modalQuery, setModalQuery] = React.useState("");
    const [modalResults, setModalResults] = React.useState<IFileItem[]>([]);
    const [modalSearching, setModalSearching] = React.useState(false);

    const [shareModal, setShareModal] = React.useState<{
        name: string;
        url: string;
        shareEnabled: boolean;
        itemId: string;
        itemType: "FILE" | "FOLDER";
    } | null>(null);
    const [copied, setCopied] = React.useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    const currentFolderId =
        folderStack.length > 0 ? folderStack[folderStack.length - 1].id : "ROOT";

    useEffect(() => {
        loadFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFolderId]);

    async function loadFiles() {
        setLoading(true);
        try {
            const data = await filesService.getFiles(currentFolderId);
            setItems(data || []);
        } catch (err) {
            console.error("Failed to load files", err);
        } finally {
            setLoading(false);
        }
    }

    async function handleModalQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
        const q = e.target.value;
        setModalQuery(q);
        if (!q.trim()) {
            setModalResults([]);
            return;
        }
        setModalSearching(true);
        try {
            const results = await filesService.searchFiles(q);
            setModalResults(results || []);
        } catch (err) {
            console.error("Search failed", err);
        } finally {
            setModalSearching(false);
        }
    }

    function handleCloseSearchModal() {
        setShowSearchModal(false);
        setModalQuery("");
        setModalResults([]);
    }

    function handleViewInFileManager(item: IFileItem) {
        handleCloseSearchModal();
        if (item.folderId === "ROOT") {
            setFolderStack([]);
            setSearchParams({});
        } else {
            setFolderStack([{ id: item.folderId, name: "..." }]);
            setSearchParams({ folderId: item.folderId });
        }
        setHighlightedItemId(item.itemId);
        setTimeout(() => setHighlightedItemId(null), 2500);
    }

    async function handleCreateFolder() {
        if (!newFolderName.trim()) return;
        try {
            await filesService.createFolder(newFolderName.trim(), currentFolderId);
            setNewFolderName("");
            setShowNewFolder(false);
            loadFiles();
        } catch (err) {
            console.error("Failed to create folder", err);
        }
    }

    async function handleShareFile(e: React.MouseEvent, item: IFileItem) {
        e.stopPropagation();
        try {
            if (item.itemType === "FILE") {
                const fileId = item.fileId!;
                const name = item.originalFilename!;
                if (item.shareEnabled && item.shareToken) {
                    const url = `${window.location.origin}/files/share/${item.shareToken}`;
                    setShareModal({ name, url, shareEnabled: true, itemId: fileId, itemType: "FILE" });
                } else {
                    const data = await filesService.generateShareToken(fileId);
                    const url = `${window.location.origin}/files/share/${data.shareToken}`;
                    setItems((prev) => prev.map((i) =>
                        i.fileId === fileId ? { ...i, shareToken: data.shareToken, shareEnabled: true } : i
                    ));
                    setShareModal({ name, url, shareEnabled: true, itemId: fileId, itemType: "FILE" });
                }
            } else {
                const folderId = item.selfFolderId!;
                const name = item.folderName!;
                if (item.shareEnabled && item.shareToken) {
                    const url = `${window.location.origin}/files/share/${item.shareToken}`;
                    setShareModal({ name, url, shareEnabled: true, itemId: folderId, itemType: "FOLDER" });
                } else {
                    const data = await filesService.generateFolderShareToken(folderId);
                    const url = `${window.location.origin}/files/share/${data.shareToken}`;
                    setItems((prev) => prev.map((i) =>
                        i.selfFolderId === folderId ? { ...i, shareToken: data.shareToken, shareEnabled: true } : i
                    ));
                    setShareModal({ name, url, shareEnabled: true, itemId: folderId, itemType: "FOLDER" });
                }
            }
            setCopied(false);
        } catch (err) {
            console.error("Failed to generate share link", err);
        }
    }

    async function handleToggleShare() {
        if (!shareModal) return;
        try {
            if (shareModal.shareEnabled) {
                if (shareModal.itemType === "FILE") {
                    await filesService.disableFileShare(shareModal.itemId);
                    setItems((prev) => prev.map((i) =>
                        i.fileId === shareModal.itemId ? { ...i, shareEnabled: false } : i
                    ));
                } else {
                    await filesService.disableFolderShare(shareModal.itemId);
                    setItems((prev) => prev.map((i) =>
                        i.selfFolderId === shareModal.itemId ? { ...i, shareEnabled: false } : i
                    ));
                }
                setShareModal((prev) => prev ? { ...prev, shareEnabled: false } : null);
            } else {
                if (shareModal.itemType === "FILE") {
                    const data = await filesService.generateShareToken(shareModal.itemId);
                    const url = `${window.location.origin}/files/share/${data.shareToken}`;
                    setItems((prev) => prev.map((i) =>
                        i.fileId === shareModal.itemId ? { ...i, shareToken: data.shareToken, shareEnabled: true } : i
                    ));
                    setShareModal((prev) => prev ? { ...prev, url, shareEnabled: true } : null);
                } else {
                    const data = await filesService.generateFolderShareToken(shareModal.itemId);
                    const url = `${window.location.origin}/files/share/${data.shareToken}`;
                    setItems((prev) => prev.map((i) =>
                        i.selfFolderId === shareModal.itemId ? { ...i, shareToken: data.shareToken, shareEnabled: true } : i
                    ));
                    setShareModal((prev) => prev ? { ...prev, url, shareEnabled: true } : null);
                }
            }
        } catch (err) {
            console.error("Failed to toggle share", err);
        }
    }

    function handleCopyLink(url: string) {
        navigator.clipboard.writeText(url);
        setCopied(true);
    }

    function handleDeleteFile(e: React.MouseEvent, fileId: string) {
        e.stopPropagation();
        confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmModal
                    title="Delete File"
                    description={[
                        { text: "Are you sure you want to delete this file?", bold: false },
                        { text: "This operation is permanent and cannot be reverted.", bold: true },
                    ]}
                    noText="Cancel"
                    yesText="Yes, Delete it!"
                    onNo={onClose}
                    onYes={async () => {
                        onClose();
                        try {
                            await filesService.deleteFile(fileId);
                            setItems((prev) => prev.filter((i) => i.fileId !== fileId));
                        } catch (err) {
                            console.error("Failed to delete file", err);
                        }
                    }}
                    danger
                />
            ),
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }

    function handleDeleteFolder(e: React.MouseEvent, folderId: string) {
        e.stopPropagation();
        confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmModal
                    title="Delete Folder"
                    description={[
                        { text: "Are you sure you want to delete this folder and all its contents?", bold: false },
                        { text: "This operation is permanent and cannot be reverted.", bold: true },
                    ]}
                    noText="Cancel"
                    yesText="Yes, Delete it!"
                    onNo={onClose}
                    onYes={async () => {
                        onClose();
                        try {
                            await filesService.deleteFolder(folderId);
                            setItems((prev) => prev.filter((i) => i.selfFolderId !== folderId));
                        } catch (err) {
                            console.error("Failed to delete folder", err);
                        }
                    }}
                    danger
                />
            ),
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }

    function handleFolderOpen(folderId: string, folderName: string) {
        const newStack = [...folderStack, { id: folderId, name: folderName }];
        setFolderStack(newStack);
        setSearchParams({ folderId });
    }

    function handleBreadcrumbNav(index: number) {
        const newStack = folderStack.slice(0, index + 1);
        setFolderStack(newStack);
        setSearchParams({ folderId: newStack[newStack.length - 1].id });
    }

    function handleRootNav() {
        setFolderStack([]);
        setSearchParams({});
    }

    function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        files.forEach((file) => startUpload(file, currentFolderId));
        e.target.value = "";
    }

    function handleFolderInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        uploadFolderFiles(files);
        e.target.value = "";
    }

    async function uploadFolderFiles(files: File[]) {
        const foldersByPath: Record<string, string> = {};

        const getOrCreateFolder = async (pathParts: string[]): Promise<string> => {
            let parentId = currentFolderId;
            for (let i = 0; i < pathParts.length; i++) {
                const partPath = pathParts.slice(0, i + 1).join("/");
                if (!foldersByPath[partPath]) {
                    const result = await filesService.createFolder(pathParts[i], parentId);
                    foldersByPath[partPath] = result.folderId;
                }
                parentId = foldersByPath[partPath];
            }
            return parentId;
        };

        for (const file of files) {
            const relativePath = (file as any).webkitRelativePath || file.name;
            const parts = relativePath.split("/");
            const folderParts = parts.slice(0, -1);
            const folderId =
                folderParts.length > 0
                    ? await getOrCreateFolder(folderParts)
                    : currentFolderId;
            startUpload(file, folderId);
        }

        loadFiles();
    }

    function startUpload(file: File, folderId: string) {
        const fileId = uuidv4();

        setUploadProgressList((prev) => [
            ...prev,
            { fileId, fileName: file.name, percentage: 0 },
        ]);

        const uploader = new GeneralMultiPartUploader({
            file,
            fileName: file.name,
            fileId,
            category,
            clientId,
            folderId,
        });

        uploader
            .getOnProgress(({ percentage }) => {
                setUploadProgressList((prev) =>
                    prev.map((p) =>
                        p.fileId === fileId ? { ...p, percentage } : p
                    )
                );
            })
            .getOnComplete(() => {
                setUploadProgressList((prev) =>
                    prev.filter((p) => p.fileId !== fileId)
                );
                loadFiles();
            })
            .getOnError((err) => {
                console.error("Upload error", err);
                setUploadProgressList((prev) =>
                    prev.filter((p) => p.fileId !== fileId)
                );
            });

        uploader.start();
    }

    function formatFileSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    }

    function renderBreadcrumb() {
        const isRoot = folderStack.length === 0;
        const showEllipsis = folderStack.length > 2;
        const visibleStack = showEllipsis ? folderStack.slice(-2) : folderStack;

        return (
            <>
                <Styled.BreadcrumbItem onClick={handleRootNav} $active={isRoot}>
                    Root
                </Styled.BreadcrumbItem>
                {showEllipsis && (
                    <>
                        <Styled.BreadcrumbSeparator>/</Styled.BreadcrumbSeparator>
                        <Styled.BreadcrumbItem>...</Styled.BreadcrumbItem>
                    </>
                )}
                {visibleStack.map((f, localIdx) => {
                    const globalIdx = showEllipsis
                        ? folderStack.length - 2 + localIdx
                        : localIdx;
                    return (
                        <React.Fragment key={f.id}>
                            <Styled.BreadcrumbSeparator>/</Styled.BreadcrumbSeparator>
                            <Styled.BreadcrumbItem
                                onClick={() => handleBreadcrumbNav(globalIdx)}
                                $active={globalIdx === folderStack.length - 1}
                            >
                                {f.name}
                            </Styled.BreadcrumbItem>
                        </React.Fragment>
                    );
                })}
            </>
        );
    }

    return (
        <Styled.StyledContainer fluid>
            <Styled.Header>
                <Styled.HeaderLeftSection>
                    <Styled.HeaderTitle>Files</Styled.HeaderTitle>
                </Styled.HeaderLeftSection>
                <Styled.HeaderRightSection>
                    <Styled.ActionButton onClick={() => setShowSearchModal(true)}>
                        <Search size={15} />
                        Search
                    </Styled.ActionButton>
                    <Styled.ActionButton onClick={() => setShowNewFolder((v) => !v)}>
                        <FolderPlus size={15} />
                        New Folder
                    </Styled.ActionButton>
                    <Styled.ActionButton onClick={() => fileInputRef.current?.click()}>
                        <CloudUpload size={15} />
                        Upload Files
                    </Styled.ActionButton>
                    <Styled.PrimaryButton onClick={() => folderInputRef.current?.click()}>
                        <Styled.ButtonContent>
                            <CloudUpload size={15} />
                            Upload Folder
                        </Styled.ButtonContent>
                    </Styled.PrimaryButton>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                    />
                    <input
                        ref={folderInputRef}
                        type="file"
                        style={{ display: "none" }}
                        // @ts-ignore
                        webkitdirectory="true"
                        onChange={handleFolderInputChange}
                    />
                </Styled.HeaderRightSection>
            </Styled.Header>

            <Styled.Description>
                Manage and organize your files. Upload files or folders, create folders, and link them to your videos.
            </Styled.Description>

            {showNewFolder && (
                <Styled.NewFolderForm>
                    <Styled.NewFolderInput
                        type="text"
                        placeholder="Folder name"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                        autoFocus
                    />
                    <Styled.PrimaryButton onClick={handleCreateFolder}>
                        <Styled.ButtonContent>Create</Styled.ButtonContent>
                    </Styled.PrimaryButton>
                    <Styled.ActionButton onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}>
                        Cancel
                    </Styled.ActionButton>
                </Styled.NewFolderForm>
            )}

            <Styled.Breadcrumb>
                {renderBreadcrumb()}
            </Styled.Breadcrumb>

            {uploadProgressList.length > 0 && (
                <Styled.UploadProgressList>
                    {uploadProgressList.map((up) => (
                        <Styled.UploadProgressItem key={up.fileId}>
                            <CloudUpload size={16} color={colors.text.green} />
                            <Styled.UploadProgressLabel>{up.fileName}</Styled.UploadProgressLabel>
                            <Styled.UploadProgressBarWrapper>
                                <Styled.UploadProgressBar $pct={up.percentage} />
                            </Styled.UploadProgressBarWrapper>
                            <Styled.UploadProgressPercent>{up.percentage}%</Styled.UploadProgressPercent>
                        </Styled.UploadProgressItem>
                    ))}
                </Styled.UploadProgressList>
            )}

            {loading ? (
                <Styled.LoadingWrapper>
                    <Spinner animation="border" />
                </Styled.LoadingWrapper>
            ) : (
                <Styled.ItemsList>
                    {items.length === 0 ? (
                        <Styled.EmptyState>
                            <Styled.EmptyStateIcon>
                                {folderStack.length > 0 ? <Folder2 /> : <Archive />}
                            </Styled.EmptyStateIcon>
                            <Styled.EmptyStateText>
                                This folder is empty. Upload some files to get started.
                            </Styled.EmptyStateText>
                        </Styled.EmptyState>
                    ) : (
                        items.map((item) => (
                            <Styled.FileCard
                                key={item.itemId}
                                $highlighted={item.itemId === highlightedItemId}
                                onClick={() =>
                                    item.itemType === "FOLDER"
                                        ? handleFolderOpen(item.selfFolderId!, item.folderName!)
                                        : undefined
                                }
                            >
                                <Styled.FileCardLeft>
                                    <Styled.FileIcon>
                                        {item.itemType === "FOLDER" ? (
                                            <Folder2 size={20} color={colors.text.green} />
                                        ) : (
                                            <FileEarmark size={20} color="#888" />
                                        )}
                                    </Styled.FileIcon>
                                    <Styled.FileInfo>
                                        {item.itemType === "FOLDER" ? (
                                            <Styled.FileName>{item.folderName}</Styled.FileName>
                                        ) : item.downloadUrl ? (
                                            <Styled.FileNameLink
                                                href={item.downloadUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {item.originalFilename}
                                            </Styled.FileNameLink>
                                        ) : (
                                            <Styled.FileName>{item.originalFilename}</Styled.FileName>
                                        )}
                                        <Styled.FileMeta>
                                            {item.itemType === "FILE" && item.objectSize
                                                ? `${formatFileSize(item.objectSize)} · `
                                                : ""}
                                            {new Date(item.createdAt).toLocaleDateString("en-US", {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                            })}
                                        </Styled.FileMeta>
                                    </Styled.FileInfo>
                                </Styled.FileCardLeft>

                                <Styled.FileCardRight>
                                    <Styled.ShareButton
                                        onClick={(e) => handleShareFile(e, item)}
                                        style={item.shareEnabled ? { opacity: 1 } : { opacity: 0.6 }}
                                    >
                                        <Share size={13} />
                                        {item.shareEnabled ? "Shared" : "Share"}
                                    </Styled.ShareButton>
                                    {item.itemType === "FILE" ? (
                                        <Styled.DangerButton
                                            onClick={(e) => handleDeleteFile(e, item.fileId!)}
                                        >
                                            <Trash size={14} />
                                            Delete
                                        </Styled.DangerButton>
                                    ) : (
                                        <Styled.DangerButton
                                            onClick={(e) => handleDeleteFolder(e, item.selfFolderId!)}
                                        >
                                            <Trash size={14} />
                                            Delete
                                        </Styled.DangerButton>
                                    )}
                                </Styled.FileCardRight>
                            </Styled.FileCard>
                        ))
                    )}
                </Styled.ItemsList>
            )}

            <Styled.ShareModal
                show={!!shareModal}
                onHide={() => setShareModal(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Share {shareModal?.itemType === "FOLDER" ? "Folder" : "File"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {shareModal && (
                        <>
                            <Styled.ShareFileName>{shareModal.name}</Styled.ShareFileName>
                            {shareModal.shareEnabled ? (
                                <>
                                    <Styled.ShareLinkRow>
                                        <Styled.ShareLinkInput
                                            readOnly
                                            value={shareModal.url}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        <Styled.CopyButton onClick={() => handleCopyLink(shareModal.url)}>
                                            Copy
                                        </Styled.CopyButton>
                                    </Styled.ShareLinkRow>
                                    {copied && <Styled.CopiedBadge>Link copied!</Styled.CopiedBadge>}
                                    <div style={{ marginTop: "12px", width: "100%" }}>
                                        <Styled.DangerButton onClick={handleToggleShare}>
                                            Disable Link
                                        </Styled.DangerButton>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ color: "#B0B0B0", fontSize: "13px", marginBottom: "12px" }}>
                                        Link sharing is disabled.
                                    </div>
                                    <div style={{ width: "100%" }}>
                                        <Styled.PrimaryButton onClick={handleToggleShare}>
                                            <Styled.ButtonContent>Enable Link</Styled.ButtonContent>
                                        </Styled.PrimaryButton>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Styled.ShareModal>

            <Styled.SearchModal
                show={showSearchModal}
                onHide={handleCloseSearchModal}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Search Files</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Styled.ModalSearchWrapper>
                        <Styled.ModalSearchIconWrapper>
                            <Search size={14} />
                        </Styled.ModalSearchIconWrapper>
                        <Styled.ModalSearchInput
                            type="text"
                            placeholder="Type a file name..."
                            value={modalQuery}
                            onChange={handleModalQueryChange}
                            autoFocus
                        />
                    </Styled.ModalSearchWrapper>

                    {modalSearching ? (
                        <Styled.ModalLoadingWrapper>
                            <Spinner animation="border" size="sm" />
                        </Styled.ModalLoadingWrapper>
                    ) : modalQuery.trim() && modalResults.length === 0 ? (
                        <Styled.ModalEmptyText>No files match your search.</Styled.ModalEmptyText>
                    ) : (
                        <Styled.ModalResultsList>
                            {modalResults.map((item) => (
                                <Styled.ModalResultItem key={item.itemId}>
                                    <Styled.ModalResultLeft>
                                        <FileEarmark size={18} color="#888" />
                                        <Styled.ModalResultInfo>
                                            <Styled.ModalResultName>
                                                {item.originalFilename}
                                            </Styled.ModalResultName>
                                            <Styled.ModalResultMeta>
                                                {item.objectSize ? `${formatFileSize(item.objectSize)} · ` : ""}
                                                {new Date(item.createdAt).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </Styled.ModalResultMeta>
                                        </Styled.ModalResultInfo>
                                    </Styled.ModalResultLeft>
                                    <Styled.ModalViewButton onClick={() => handleViewInFileManager(item)}>
                                        View in File Manager
                                    </Styled.ModalViewButton>
                                </Styled.ModalResultItem>
                            ))}
                        </Styled.ModalResultsList>
                    )}
                </Modal.Body>
            </Styled.SearchModal>
        </Styled.StyledContainer>
    );
};
